/* eslint-disable camelcase */
import * as React from 'react';

import Avatar from 'components/Avatar';
import AvatarContactabilityBadge from 'components/AvatarContactabilityBadge';
import AvatarReachabilityBadge from 'components/AvatarReachabilityBadge';
import { AvatarSize } from 'components/Avatar';
import { ContactabilityStatus } from 'globalTypes';
import Feature from 'components/Feature';
import { ReachabilityInfo } from 'components/ReachabilityBadge';

// This type does not protect against accessing fields that do not exist
// because a User exists in way too many forms across frontend
export type UnsafePartialUser = Partial<{
  initials?: string;
  profilePictureUrl?: string;
  contactabilityStatus?: ContactabilityStatus;
  profile_picture_url?: string;
  profile_picture?: {
    urls: {
      __typename: 'FileUrls';
      cdn: string | null;
    };
  } | null;
  contactability_status?: ContactabilityStatus;
  avatarUrl?: string;
  reachabilityInfo?: ReachabilityInfo;
  reachability_info?: ReachabilityInfo;
  [key: string]: any;
}>;

export type Props = {
  user: UnsafePartialUser | null;
  color?: 'brand' | 'grey' | 'fullGrey' | 'white';
  size?: AvatarSize;
  className?: string;
  hideBadge?: boolean;
  disableBadgeHover?: boolean;
  onClick?: (event: React.SyntheticEvent) => any;
};

/* Pass a JSON user into this component and it will correctly render
 * an Avatar based on the available data */

export default class UserAvatar extends React.Component<Props> {
  render() {
    const {
      color,
      size,
      className,
      user,
      hideBadge,
      disableBadgeHover,
      ...otherProps
    } = this.props;
    const blankAvatar = (
      <Avatar color={color} size={size} className={className} text="??" {...otherProps} />
    );

    if (!user) {
      return blankAvatar;
    }

    const {
      initials,
      profilePictureUrl,
      profile_picture_url,
      contactabilityStatus,
      contactability_status,
      avatarUrl,
      profile_picture,
      reachabilityInfo,
      reachability_info,
    } = user;

    const contactabilityBadge =
      contactabilityStatus || contactability_status ? (
        <AvatarContactabilityBadge
          hoverable={!disableBadgeHover}
          status={contactabilityStatus || contactability_status}
        />
      ) : (
        undefined
      );

    const reachabilityBadge =
      reachabilityInfo || reachability_info ? (
        <AvatarReachabilityBadge
          hoverable={!disableBadgeHover}
          reachability={reachabilityInfo || reachability_info}
        />
      ) : (
        undefined
      );

    const badge = hideBadge ? (
      undefined
    ) : (
      <Feature
        name="2018.oct.web.reachability_status"
        on={reachabilityBadge}
        off={contactabilityBadge}
      />
    );

    if (profile_picture) {
      return (
        <Avatar
          color={color}
          size={size}
          className={className}
          image={profile_picture.urls.cdn ?? undefined}
          badge={badge}
          {...otherProps}
        />
      );
    }

    if (profilePictureUrl || profile_picture_url || avatarUrl) {
      return (
        <Avatar
          color={color}
          size={size}
          className={className}
          image={profilePictureUrl || profile_picture_url || avatarUrl}
          badge={badge}
          {...otherProps}
        />
      );
    }

    if (initials) {
      return (
        <Avatar
          color={color}
          size={size}
          className={className}
          text={initials}
          badge={badge}
          {...otherProps}
        />
      );
    }

    return blankAvatar;
  }
}
